import React from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/pro-solid-svg-icons';
import { Typography, Button } from 'src/components';
import { UserMultiSelect } from '../UserMultiSelect';
import { getInitials } from 'src/utils/getInitials/getInitials';

interface TeamMembersSectionProps {
  teamData: any;
  isLoading: boolean;
  isEditingMembers: boolean;
  isUpdating: boolean;
  selectedUsers: string[];
  setIsEditingMembers: (isEditing: boolean) => void;
  resetSelectedUsers: () => void;
  handleUserSelectionChange: (users: string[]) => void;
  handleSaveTeamMembers: () => void;
  disableEditing?: boolean; // Optional prop to disable editing
}

const TeamMembersSection: React.FC<TeamMembersSectionProps> = ({
  teamData,
  isLoading,
  isEditingMembers,
  isUpdating,
  selectedUsers,
  setIsEditingMembers,
  resetSelectedUsers,
  handleUserSelectionChange,
  handleSaveTeamMembers,
  disableEditing = false, // Default to false if not provided
}) => {
  // Sort users alphabetically by name
  const sortedUsers = teamData?.users
    ? [...teamData.users].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      })
    : [];

  const avatarRender = (member: any) => {
    if (member.first_name && member.last_name) {
      return getInitials(member.first_name, member.last_name);
    }
    return member.username.charAt(0).toUpperCase();
  };

  return (
    <div className="flex-1 border border-neutral-200 rounded-lg p-4">
      <div className="flex justify-between items-center mb-4">
        <Typography variant="h5" className="text-neutral-700">
          Team Members
        </Typography>
        <div className="flex items-center">
          <Typography className="text-xs font-medium text-neutral-700 bg-neutral-100 px-2 py-1 rounded-full mr-4">
            {sortedUsers.length} member
            {sortedUsers.length === 1 ? '' : 's'}
          </Typography>
          {!isEditingMembers ? (
            // Show Edit Members button only if editing is not disabled
            !disableEditing ? (
              <Button
                variant="solid"
                color="neutral"
                size="sm"
                startDecorator={<Icon icon={faPencil} />}
                onClick={() => setIsEditingMembers(true)}
                data-testid="edit-team-members-button"
              >
                Edit Members
              </Button>
            ) : null
          ) : (
            // Show Save/Cancel buttons only when in edit mode
            <div className="flex gap-2">
              <Button
                variant="outlined"
                color="neutral"
                size="sm"
                onClick={() => {
                  setIsEditingMembers(false);
                  resetSelectedUsers();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="primary"
                size="sm"
                onClick={handleSaveTeamMembers}
                disabled={isUpdating}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      {isEditingMembers ? (
        <div className="p-4 border border-neutral-200 rounded-lg">
          <UserMultiSelect
            selectedUsers={selectedUsers}
            onChange={handleUserSelectionChange}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-h-[60vh] overflow-auto">
          {isLoading ? (
            <div className="flex justify-center p-4">
              <Typography>Loading team members...</Typography>
            </div>
          ) : sortedUsers.length ? (
            sortedUsers.map((member: any) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center text-sm text-neutral-700 font-medium">
                    {avatarRender(member)}
                  </div>
                  <div className="flex flex-col">
                    <Typography
                      variant="body1"
                      className="font-medium text-neutral-800"
                    >
                      {member.first_name} {member.last_name}
                    </Typography>
                    <Typography variant="body2" className="text-neutral-500">
                      {member.username}
                    </Typography>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="flex justify-center p-4 border border-neutral-200 rounded-lg">
              <Typography className="text-neutral-500">
                No team members found.
              </Typography>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMembersSection;
